<template>
  <div class="search-view">
    <!-- Header -->
    <div class="d-flex align-center justify-space-between mb-6">
      <div class="d-flex align-center ga-3">
        <v-btn
          to="/comics/homeview"
          icon
          variant="tonal"
          size="small"
          aria-label="Back"
        >
          <v-icon size="20">mdi-arrow-left</v-icon>
        </v-btn>
        <div>
          <h1 class="text-h4 font-weight-bold">Search Comics</h1>
          <p class="text-caption text-medium-emphasis mb-0">Find graphic novels, manga, and comic issues</p>
        </div>
      </div>
    </div>

    <!-- Search Form Card -->
    <v-card class="glass-card pa-6 mb-6">
      <v-form @submit.prevent="runSearch">
        <v-row align="center" dense>
          <v-col cols="12" sm="8" md="9">
            <v-text-field
              v-model="query"
              placeholder="Search by title, author, artist, series, publisher..."
              prepend-inner-icon="mdi-magnify"
              density="comfortable"
              variant="outlined"
              clearable
              hide-details
              autofocus
            />
          </v-col>
          <v-col cols="12" sm="4" md="3">
            <v-btn
              type="submit"
              color="#f59e0b"
              size="large"
              block
              class="btn-modern text-white font-weight-bold"
              :loading="loading"
              prepend-icon="mdi-magnify"
            >
              Search
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
    </v-card>

    <!-- Error Alert -->
    <v-alert v-if="error" type="error" class="mb-6" variant="tonal">{{ error }}</v-alert>

    <!-- Results Header -->
    <div v-if="searched && !loading" class="d-flex align-center justify-space-between mb-4">
      <div class="text-subtitle-1 font-weight-bold">
        Results for "<span style="color: #f59e0b">{{ lastQuery }}</span>"
      </div>
      <v-chip size="small" color="#f59e0b" variant="tonal" class="font-weight-bold">
        {{ results.length }} comic{{ results.length === 1 ? '' : 's' }} found
      </v-chip>
    </div>

    <!-- Results Grid -->
    <div v-if="results.length" class="poster-dense-grid">
      <div
        v-for="post in results"
        :key="post.id"
        class="grid-poster-item"
      >
        <router-link
          :to="{ name: 'ComicDetail', params: { id: post.id } }"
          class="poster-card poster-card--comics d-block text-decoration-none"
        >
          <div class="grid-poster-container">
            <v-img
              :src="posterSrc(post)"
              :aspect-ratio="2 / 3"
              cover
              class="rounded-lg w-100"
            >
              <template #placeholder>
                <div class="d-flex flex-column align-center justify-center fill-height fallback-poster">
                  <v-icon size="36" color="#f59e0b">mdi-draw</v-icon>
                  <span class="text-caption text-center px-2 mt-1 text-truncate w-100 font-weight-medium">
                    {{ post.title }}
                  </span>
                </div>
              </template>
            </v-img>

            <div class="poster-badges-top">
              <div v-if="post.rating && Number(post.rating) > 0" class="rating-badge">
                <v-icon size="12" color="amber" class="mr-0.5">mdi-star</v-icon>
                <span>{{ post.rating }}</span>
              </div>
              <div v-if="post.issue_number" class="issue-badge">
                #{{ post.issue_number }}
              </div>
            </div>

            <div class="poster-overlay-bottom">
              <div class="poster-title text-truncate font-weight-bold">{{ post.title }}</div>
              <div class="poster-subtitle d-flex justify-space-between align-center">
                <span v-if="post.publisher" class="text-truncate">{{ post.publisher }}</span>
                <span v-else-if="post.author" class="text-truncate">{{ post.author }}</span>
                <v-chip size="x-small" variant="tonal" color="white" class="ml-auto">
                  {{ post.category }}
                </v-chip>
              </div>
            </div>
          </div>
        </router-link>
      </div>
    </div>

    <!-- Empty Results -->
    <v-card v-else-if="searched && !loading" class="glass-card pa-12 text-center my-6">
      <v-icon size="64" color="#f59e0b" class="mb-4">mdi-text-search</v-icon>
      <h3 class="text-h5 font-weight-bold mb-2">No Matching Comics</h3>
      <p class="text-body-1 text-medium-emphasis mb-0">
        We couldn't find any comics matching "{{ lastQuery }}". Try a different title or author.
      </p>
    </v-card>
  </div>
</template>

<script>
import { ref } from 'vue'
import ComicsAPI from '@/services/comics'
import { posterSrc } from '@/utils/comicForm'

export default {
  name: 'ComicSearch',
  setup() {
    const query = ref('')
    const lastQuery = ref('')
    const results = ref([])
    const loading = ref(false)
    const error = ref('')
    const searched = ref(false)

    const runSearch = async () => {
      if (!query.value || !query.value.trim()) return
      loading.value = true
      error.value = ''
      lastQuery.value = query.value.trim()
      searched.value = true
      try {
        const { data } = await ComicsAPI.search(lastQuery.value)
        results.value = data
      } catch (err) {
        error.value = err.response?.data?.detail?.message || 'Search failed'
        results.value = []
      } finally {
        loading.value = false
      }
    }

    return { query, lastQuery, results, loading, error, searched, runSearch, posterSrc }
  },
}
</script>

<style scoped>
.search-view {
  max-width: 1240px;
  margin: 0 auto;
}

.poster-dense-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 16px;
}

@media (max-width: 600px) {
  .poster-dense-grid {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 12px;
  }
}

.grid-poster-container {
  position: relative;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  background: rgba(128, 128, 128, 0.08);
}

.fallback-poster {
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.15), rgba(251, 191, 36, 0.1));
}

.poster-badges-top {
  position: absolute;
  top: 8px;
  left: 8px;
  right: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  pointer-events: none;
  z-index: 2;
}

.rating-badge {
  display: inline-flex;
  align-items: center;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(8px);
  color: #fff;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 6px;
}

.issue-badge {
  background: rgba(245, 158, 11, 0.9);
  backdrop-filter: blur(8px);
  color: #000;
  font-size: 0.65rem;
  font-weight: 800;
  padding: 2px 5px;
  border-radius: 4px;
  margin-left: auto;
}

.poster-overlay-bottom {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 24px 8px 8px 8px;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.6) 60%, transparent 100%);
  color: #fff;
  z-index: 2;
}

.poster-title {
  font-size: 0.8rem;
  line-height: 1.2;
}

.poster-subtitle {
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.75);
  margin-top: 2px;
}
</style>

